import React, { useState } from 'react';
import { FragmentData, gql } from '@ts-gql/tag';
import { useMutation } from '@ts-gql/apollo';

import { Drawer } from '@reckon-web/drawer';
import { Stack } from '@reckon-web/stack';
import {
  field,
  useForm,
  useIsFormEqualToInitialValue,
  useSubmit,
} from '@reckon-web/forms';
import { Box } from '@reckon-web/box';
import { Text } from '@reckon-web/text';
import { Flex } from '@reckon-web/flex';
import { Checkbox } from '@reckon-web/checkbox';
import { Radio } from '@reckon-web/radio';
import { UserAvatar } from '@reckon-web/avatar';
import { Heading } from '@reckon-web/heading';
import { IconButton } from '@reckon-web/button';
import { DropdownMenu, MenuItem } from '@reckon-web/dropdown';
import { useToasts } from '@reckon-web/toast';
import { Badge } from '@reckon-web/badge';
import { MoreHorizontalIcon, RotateCcwIcon, SendIcon } from '@reckon-web/icon';

import {
  required,
  requiredEmail,
  trimspace,
} from '../../../utilities/validators';
import { TextField } from '../../design-system/text-field';
import { CreateEditProps, Product, Role } from '../types';

import { ResetMfaDialog } from './moreOptionsButton/ResetMfaDialog';
import { ResendInviteDialog } from './moreOptionsButton/ResendInviteDialog';

let productFragment = gql`
  fragment UserProducts_products on UserProducts {
    productId
    friendlyName
    internalName
  }
` as import('../../../../__generated__/ts-gql/UserProducts_products').type;

const userFormSchema = field.object(
  {
    firstName: field.text({
      validate: required(),
    }),
    lastName: field.text(),
    email: field.text({
      validate: requiredEmail(),
    }),
    partner: field.boolean(),
    manager: field.boolean(),
    roleName: field.text(),
    products: field.multiselect(),
  },
  {
    validate: {
      firstName: trimspace(),
      lastName: trimspace(),
    },
  }
);

export function CreateEditUserDrawer({
  setDrawerOpen,
  editUser,
  setEditUser,
  roleList,
  productList,
  canEditUser,
}: CreateEditProps) {
  const { addToast } = useToasts();

  let firstName = '';
  let lastName = '';

  const nameArray =
    (editUser && (editUser.fullName as string).split(' ')) || [];

  if (nameArray.length > 1) {
    lastName = nameArray.pop() || '';
  }
  firstName = nameArray.join(' ') || '';

  let form = useForm(userFormSchema, {
    firstName,
    lastName,
    email: editUser?.email?.toLowerCase() || '',
    partner: editUser?.partner || false,
    manager: editUser?.manager || false,
    roleName: editUser?.roleName || 'user',
    products:
      (editUser &&
        (editUser?.products as FragmentData<typeof productFragment>[]).map(
          (i: any) => i.productId
        )) ||
      [],
  });

  const isEqualToInitValue = useIsFormEqualToInitialValue(form);

  const [createUser, createStatus] = useMutation(CREATE_USER);
  const [updateUser, updateStatus] = useMutation(UPDATE_USER);
  const [isResendDialogOpen, setResendDialogOpen] = useState(false);
  const [isResetMfaDialogOpen, setResetMfaDialogOpen] = useState(false);

  const successToast = (fullName: string) => {
    addToast({
      title: editUser ? 'User update successful' : 'Create user successful',
      message: `User ${fullName} has been ${editUser ? 'updated' : 'created'}`,
      tone: 'positive',
    });
  };

  const errorToast = (err: Error) => {
    let message = err?.message
      ? err.message
      : `Unable to ${
          editUser ? 'update' : 'create'
        } the user. Please try again. If the problem persists please contact support`;

    addToast({
      title: 'Something went wrong',
      message,
      tone: 'critical',
    });
  };

  const submit = useSubmit(form, (values) => {
    const input = {
      fullName: `${values.firstName} ${values.lastName}`,
      email: values.email,
      active: editUser?.active || true,
      roleName: values.roleName,
      client: 'TUM',
      products: productList
        .filter((p: Product) => values.products.includes(p.productId))
        .map((p: any) => {
          return {
            friendlyName: p?.friendlyName,
            internalName: p?.internalName,
            productId: p?.productId,
          };
        }),
      partner: values.partner,
      manager: values.manager,
    };

    editUser
      ? updateUser({
          variables: {
            id: editUser.cloudId,
            input,
          },
          refetchQueries: ['TenantRolesAndProducts', 'TenantUserList'],
        })
          .then(() => {
            successToast(input?.fullName);
            setDrawerOpen(false);
            setEditUser(null);
          })
          .catch((err) => {
            errorToast(err);
          })
      : createUser({
          variables: {
            input,
          },
          refetchQueries: ['TenantRolesAndProducts', 'TenantUserList'],
        })
          .then(() => {
            successToast(input?.fullName);
            setDrawerOpen(false);
          })
          .catch((err) => {
            errorToast(err);
          });
  });

  const isMfaEnabled = editUser?.mfaRequired;

  return (
    <Drawer
      shouldShowCancelConfirmationDialog={!isEqualToInitValue}
      type="form"
      title={editUser ? '' : 'Create User'}
      actions={
        canEditUser
          ? {
              confirm: {
                action: submit,
                label: 'Save & close',
                loading: createStatus.loading || updateStatus.loading,
              },
              cancel: {
                action: () => {
                  setEditUser(null);
                  setDrawerOpen(false);
                },
                label: 'Discard',
              },
            }
          : {
              confirm: {
                action: () => {
                  setEditUser(null);
                  setDrawerOpen(false);
                },
                label: 'close',
              },
            }
      }
    >
      {editUser && (
        <Flex width="100%" justifyContent="flex-end">
          <DropdownMenu
            trigger={({ triggerProps }) => (
              <IconButton
                icon={MoreHorizontalIcon}
                label="More options"
                weight="subtle"
                {...triggerProps}
              />
            )}
          >
            <MenuItem
              icon={SendIcon}
              label="Resend invite"
              onClick={() => setResendDialogOpen(true)}
              tone="active"
            />

            <MenuItem
              disabled={!isMfaEnabled}
              icon={RotateCcwIcon}
              label="Reset MFA"
              onClick={() => setResetMfaDialogOpen(true)}
              tone={isMfaEnabled ? 'active' : 'passive'}
            />
          </DropdownMenu>

          <ResendInviteDialog
            editUser={editUser}
            isResendDialogOpen={isResendDialogOpen}
            setResendDialogOpen={setResendDialogOpen}
          />

          <ResetMfaDialog
            editUser={editUser}
            isResetMfaDialogOpen={isResetMfaDialogOpen}
            setResetMfaDialogOpen={setResetMfaDialogOpen}
          />
        </Flex>
      )}

      {editUser && (
        <Stack align="center" gap="medium">
          <UserAvatar
            name={`${form.fields.firstName.value} ${form.fields.lastName.value}`}
            size="large"
          />
          <Heading level="2">{`${form.fields.firstName.value} ${form.fields.lastName.value}`}</Heading>
          <Text color="dim" size="small">
            Created{' '}
            {editUser.createdOn &&
              new Date(editUser.createdOn).toLocaleDateString('en-NZ', {
                // @ts-ignore
                dateStyle: 'long',
              })}
          </Text>
        </Stack>
      )}
      <Stack dividers gap="large">
        <div>
          <Text weight="semibold">DETAILS</Text>
          <Flex marginY="medium">
            <Box width="50%" marginRight="medium">
              <TextField
                {...form.fields.firstName.props}
                label="First name *"
                placeholder="First name"
                disabled={!canEditUser}
              />
            </Box>
            <Box width="50%">
              <TextField
                {...form.fields.lastName.props}
                label="Last name"
                placeholder="Last name"
                disabled={!canEditUser}
              />
            </Box>
          </Flex>
          <TextField
            {...form.fields.email.props}
            label="Email address *"
            placeholder="Email address"
            disabled={!canEditUser}
            onChange={(e) => {
              form.fields.email.setState({
                ...form.fields.email.state,
                value: e.target?.value?.toLowerCase(),
              });
            }}
          />
        </div>

        <Stack gap="small">
          <Text weight="semibold">ROLE</Text>
          <Box>
            <Checkbox
              disabled={!canEditUser}
              checked={form.fields.partner.props.value}
              onChange={(e) => {
                const isChecked = e.target.checked;
                form.fields.partner.setState({
                  ...form.fields.partner.state,
                  value: isChecked,
                });
              }}
            >
              Partner
            </Checkbox>
          </Box>
          <Box>
            <Checkbox
              disabled={!canEditUser}
              checked={form.fields.manager.props.value}
              onChange={(e) => {
                const isChecked = e.target.checked;
                form.fields.manager.setState({
                  ...form.fields.manager.state,
                  value: isChecked,
                });
              }}
            >
              Manager
            </Checkbox>
          </Box>
        </Stack>

        <Stack gap="small">
          <Box>
            <Text weight="semibold">POLICY</Text>
          </Box>
          {roleList &&
            roleList.map((role: Role) => (
              <Box key={role.roleName}>
                <Radio
                  disabled={!canEditUser}
                  checked={role.roleName === form.fields.roleName.value}
                  value={role.roleName}
                  onChange={(e) => {
                    form.fields.roleName.setState({
                      ...form.fields.roleName.state,
                      value: e.target.value,
                    });
                  }}
                >
                  {role.roleDescription}
                </Radio>
              </Box>
            ))}
        </Stack>

        <Stack gap="small">
          <Box>
            <Text weight="semibold">PRODUCTS</Text>
          </Box>
          {productList &&
            productList.map((product: Product) => (
              <Box key={product.productId}>
                <Checkbox
                  disabled={!canEditUser}
                  checked={form.fields.products.value.includes(
                    product.productId
                  )}
                  name={product.productId}
                  onChange={(e) => {
                    const { name, checked } = e.target;
                    const products = [...form.fields.products.value];
                    const prodIndex = products.findIndex((p) => p === name);

                    checked
                      ? products.push(name)
                      : products.splice(prodIndex, 1);

                    form.fields.products.setState({
                      ...form.fields.products.state,
                      value: products,
                    });
                  }}
                >
                  {product.friendlyName}
                </Checkbox>
              </Box>
            ))}
        </Stack>

        {editUser && (
          <Stack marginBottom="medium" gap="small">
            <Box>
              <Text weight="semibold">MFA</Text>
            </Box>
            <Box>
              <Badge
                label={isMfaEnabled ? 'Enabled' : 'Disabled'}
                tone={isMfaEnabled ? 'positive' : 'critical'}
              />
            </Box>
          </Stack>
        )}
      </Stack>
    </Drawer>
  );
}

const UPDATE_USER = gql`
  mutation UpdateUser($id: String, $input: TenantUserCreateUpdateInput!) {
    createUpdateTenantUser(inputCloudId: $id, inputUserData: $input) {
      cloudId
      actionPerformed
    }
  }
` as import('../../../../__generated__/ts-gql/UpdateUser').type;

const CREATE_USER = gql`
  mutation CreateUser($input: TenantUserCreateUpdateInput!) {
    createUpdateTenantUser(inputUserData: $input) {
      cloudId
      actionPerformed
    }
  }
` as import('../../../../__generated__/ts-gql/CreateUser').type;
